/**
 * Single app-wide notification transport: one snapshot query + one WebSocket subscription,
 * feeding the ui-toolkit state engine. Every consumer (dock, progress selectors) reads the
 * resulting activities from context instead of opening its own stream.
 */
import { createContext, useCallback, useContext, useEffect, type ReactNode } from 'react';
import { useQuery, useSubscription } from '@apollo/client/react';
import { noop } from 'es-toolkit';
import { useNotificationsState } from '@insio/ui-toolkit';
import type { NotificationView } from '@insio/ui-toolkit';
import { NotificationsDocument, NotificationReceivedDocument } from '@/graphql';
import { useLiveResync } from '@/hooks/use-live-resync';

/** `titleParams` arrives as the untyped JSON scalar; the view contract narrows it. */
function toView<T extends { titleParams?: unknown }>(notification: T) {
  return {
    ...notification,
    titleParams: (notification.titleParams ?? null) as NotificationView['titleParams']
  };
}

const NotificationActivitiesContext = createContext<NotificationView[] | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { activities, seed, ingest, resync } = useNotificationsState();

  const { data, refetch } = useQuery(NotificationsDocument, { fetchPolicy: 'no-cache' });
  useEffect(() => {
    if (data) seed(data.notifications.map(toView));
  }, [data, seed]);

  // Events sent while the socket was down or the tab asleep are lost; the snapshot is the truth.
  const resyncSnapshot = useCallback(() => {
    // A failed re-read (still offline) keeps the current state; the next trigger retries.
    refetch()
      .then(result => {
        if (result.data) resync(result.data.notifications.map(toView));
      })
      .catch(noop);
  }, [refetch, resync]);
  useLiveResync(resyncSnapshot);

  useSubscription(NotificationReceivedDocument, {
    onData: ({ data: result }) => {
      const notification = result.data?.notificationReceived;
      if (notification) ingest(toView(notification));
    }
  });

  return (
    <NotificationActivitiesContext.Provider value={activities}>
      {children}
    </NotificationActivitiesContext.Provider>
  );
}

/** Live notification activities from the shared stream. */
export function useNotificationActivities(): NotificationView[] {
  const activities = useContext(NotificationActivitiesContext);
  if (activities === null) {
    throw new Error('useNotificationActivities must be used within NotificationsProvider');
  }
  return activities;
}
